import React, { useState, useEffect, useMemo } from "react";
import "./ISFAuditLog.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ExcelJS from "exceljs";
import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";

function formatActionType(actionType) {
  if (!actionType) return "Activity";
  const t = String(actionType);
  const map = {
    SaveChildFolder: "Document Uploaded",
    SaveFolder: "Folder Created",
    DeleteFolder: "Folder Deleted",
    RestoreFolder: "Folder Restored",
    MoveDocumentToFolder: "Document Moved",
    MoveFolder: "Folder Moved",
    FileComment: "Comment Added",
    SaveFileSignature: "Document Signed",
    RestoreDocument: "Document Restored",
    SupersedeDocument: "Document Superseded",
    GetDocumentEventLogData: "Document Event",
    GetFolderEventLogData: "Folder Event",
  };
  return map[t] || t.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
}

/**
 * Normalize value changes from backend. Backend may send ValueChanges or ValueChangesJson.
 * Only render value changes when the backend sends this data; do not build from other columns.
 */
function getValueChangesFromBackend(row) {
  const raw = row.ValueChanges ?? row.ValueChangesJson ?? row.ValueChangeData;
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function mapEventLogRowToAuditLog(row) {
  const isDocument = row.IsDocument === 1 || row.IsDocument === true;
  const fileName = row.FileName != null && String(row.FileName).trim() !== "" ? row.FileName : "Document";
  const folderName = row.FolderName != null && String(row.FolderName).trim() !== "" ? row.FolderName : null;
  const entityName = isDocument
    ? (folderName ? `${folderName}/${fileName}` : fileName)
    : (row.FolderName || "Folder");
  const entityId = isDocument ? row.DocumentIdRef : row.FolderIdRef;
  const versionNo = row.VersionNo != null && String(row.VersionNo).trim() !== "" ? row.VersionNo : null;
  return {
    LogId: row.EISFLogId,
    ActivityName: formatActionType(row.ActionType),
    ActivityDoneById: row.ActionDoneBy,
    ActivityDoneBy: row.UserName || "Unknown",
    ActivityDateTime: row.LogDateTime,
    EntityName: entityName,
    EntityId: entityId,
    EntityType: isDocument ? "Document" : "Folder",
    FolderName: isDocument ? folderName : null,
    VersionNo: isDocument ? versionNo : null,
    ActivityReason: row.ActionComment && String(row.ActionComment).trim() !== "" ? row.ActionComment : null,
    ModuleName: "EISF",
    OrganizationIdRef: row.OrganizationIdRef,
    ValueChanges: getValueChangesFromBackend(row),
  };
}

async function fetchEventLogs({ organizationId, folderId, documentId, studyId }) {
  const useFolderApi = folderId != null && String(folderId).trim() !== "";
  const useDocumentApi = documentId != null && String(documentId).trim() !== "";
  const actionType = useFolderApi ? "GetFolderEventLogData" : "GetDocumentEventLogData";
  const response = await apiClient.post(API_ENDPOINTS.ISF.GET_ISF_EVENT_LOG, {
    OrganizationIdRef: organizationId || "",
    ActionType: actionType,
    DocumentIdRef: useDocumentApi ? documentId : null,
    FolderIdRef: useFolderApi ? folderId : null,
    StudyIdRef: studyId || null,
  });
  const raw = response?.data?.EventLogTable ?? response;
  const rawList = Array.isArray(raw) ? raw : (raw?.Table1 ?? raw?.Table0 ?? raw?.Data ?? []);
  const list = Array.isArray(rawList) ? rawList : [];
  return list.map(mapEventLogRowToAuditLog);
}

const EXPORT_COLUMNS = [
  "StudyName",
  "FolderName",
  "DocumentName",
  "EventType",
  "EventBy",
  "EventDateTime",
  "EventComment",
  "RestoredTo",
  "MovedTo",
  "MovedFrom",
  "SupersededBy",
  "SupersededDateTime",
  "VersionNo",
  "Signed",
  "ProtocolSigned",
  "SignatureDateTime",
];

async function fetchExportData({ organizationId, folderId, documentId, studyId }) {
  const useFolderApi = folderId != null && String(folderId).trim() !== "";
  const actionType = useFolderApi ? "GetFolderEventLogDataExport" : "GetDocumentEventLogDataExport";
  const response = await apiClient.post(API_ENDPOINTS.ISF.GET_ISF_EVENT_LOG, {
    OrganizationIdRef: organizationId || "",
    ActionType: actionType,
    DocumentIdRef: documentId || null,
    FolderIdRef: folderId || null,
    StudyIdRef: studyId || null,
  });
  const raw = response?.data?.EventLogTable ?? response?.data ?? response;
  const rows = Array.isArray(raw) ? raw : (raw?.Table1 ?? raw?.Table0 ?? raw?.Data ?? []);
  return Array.isArray(rows) ? rows : [];
}

const DEFAULT_SAMPLE_DATA = [
  // {
  //   LogId: "BF5629D2-0691-4022-9EC6-DDA0DA17A316",
  //   ActivityName: "Document Uploaded",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Dr. Sarah Mitchell",
  //   ActivityDateTime: new Date().toISOString(),
  //   EntityName: "test-6-sample.pdf",
  //   EntityId: "77C43FBB-C192-4949-8D8F-9C04CC8735CB",
  //   EntityType: "Document",
  //   ActivityReason: null,
  //   ActivityFilterDateTime: new Date().toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {
  //     FileName: { old: null, new: "test-6-sample.pdf" },
  //     FileSize: { old: null, new: "245KB" },
  //     VersionNo: { old: null, new: "1" },
  //   },
  // },
  // {
  //   LogId: "A1B2C3D4-5678-90EF-GHIJ-KLMNOPQRSTUV",
  //   ActivityName: "Folder Created",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Nurse James Chen",
  //   ActivityDateTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "Regulatory Documents",
  //   EntityId: "FOLDER-123-456",
  //   EntityType: "Folder",
  //   ActivityReason: "Created for regulatory compliance documentation",
  //   ActivityFilterDateTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {
  //     FolderName: { old: null, new: "Regulatory Documents" },
  //     ParentFolderId: { old: null, new: "ROOT" },
  //   },
  // },
  // {
  //   LogId: "X9Y8Z7W6-V5U4-T3S2-R1Q0-PONMLKJIHGF",
  //   ActivityName: "Document Signed",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Dr. Michael Brown",
  //   ActivityDateTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "protocol-v2.pdf",
  //   EntityId: "DOC-789-ABC",
  //   EntityType: "Document",
  //   ActivityReason: "Protocol approval signature required for study initiation",
  //   ActivityFilterDateTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {
  //     Signed: { old: "0", new: "1" },
  //     SignedDate: { old: null, new: new Date().toISOString() },
  //     SignedBy: { old: null, new: "Dr. Michael Brown" },
  //   },
  // },
  // {
  //   LogId: "M1N2O3P4-Q5R6-S7T8-U9V0-WXYZABCDEFGH",
  //   ActivityName: "Document Deleted",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Admin User",
  //   ActivityDateTime: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "old-document.pdf",
  //   EntityId: "DOC-OLD-123",
  //   EntityType: "Document",
  //   ActivityReason: "Superseded by new version",
  //   ActivityFilterDateTime: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {
  //     FileStatus: { old: "0", new: "1" },
  //     DeletedDate: { old: null, new: new Date().toISOString() },
  //   },
  // },
  // {
  //   LogId: "K1L2M3N4-O5P6-Q7R8-S9T0-UVWXYZABCDEF",
  //   ActivityName: "Folder Renamed",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Manager User",
  //   ActivityDateTime: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "Updated Folder Name",
  //   EntityId: "FOLDER-UPD-456",
  //   EntityType: "Folder",
  //   ActivityReason: "Better naming convention for clarity",
  //   ActivityFilterDateTime: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {
  //     FolderName: { old: "Old Folder Name", new: "Updated Folder Name" },
  //   },
  // },
  // // Sample entries with ValueChanges length 0 (no expand / value changes section)
  // {
  //   LogId: "V1W2X3Y4-Z5A6-B7C8-D9E0-FGHIJKLMNOP",
  //   ActivityName: "Document Viewed",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Dr. Sarah Mitchell",
  //   ActivityDateTime: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "protocol-v1.pdf",
  //   EntityId: "DOC-VIEW-001",
  //   EntityType: "Document",
  //   ActivityReason: null,
  //   ActivityFilterDateTime: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {},
  // },
  // {
  //   LogId: "Q1R2S3T4-U5V6-W7X8-Y9Z0-ABCDEFGHIJKL",
  //   ActivityName: "Folder Opened",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Nurse James Chen",
  //   ActivityDateTime: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "Trial Master File",
  //   EntityId: "FOLDER-OPEN-002",
  //   EntityType: "Folder",
  //   ActivityReason: "Review for compliance check",
  //   ActivityFilterDateTime: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {},
  // },
  // {
  //   LogId: "M1N2O3P4-Q5R6-S7T8-U9V0-WXYZ12345678",
  //   ActivityName: "Comment Added",
  //   ActivityDoneById: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ActivityDoneBy: "Dr. Michael Brown",
  //   ActivityDateTime: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  //   EntityName: "informed-consent.pdf",
  //   EntityId: "DOC-COMM-003",
  //   EntityType: "Document",
  //   ActivityReason: "Noted typo on page 2",
  //   ActivityFilterDateTime: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  //   ModuleName: "EISF",
  //   OrganizationId: "9D02BFC5-7030-4CD7-B21B-2B8EE6C79055",
  //   ValueChanges: {},
  // },
];

const ISFAuditLog = ({
  variant = "modal",
  isOpen,
  onClose,
  folderId = null,
  documentId = null,
  studyId = null,
  organizationId = "",
  showFilters = true,
  valueChangesKey = "ValueChanges",
}) => {
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const shouldFetch = variant === "modal" ? isOpen : true;
  useEffect(() => {
    if (!shouldFetch || !organizationId) {
      if (!shouldFetch) setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    fetchEventLogs({ organizationId, folderId, documentId, studyId })
      .then((list) => {
        setData(list);
      })
      .catch((err) => {
        console.error("Error fetching ISF event logs:", err);
        setError(err?.message || "Failed to load audit logs");
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [shouldFetch, organizationId, folderId, documentId, studyId]);

  const auditLogs = Array.isArray(data) ? data : [];

  const hasMeaningfulValueChanges = (valueChanges) => {
    if (!valueChanges || typeof valueChanges !== "object" || Object.keys(valueChanges).length === 0)
      return false;
    return Object.values(valueChanges).some(
      (ch) => ch != null && (ch.old != null || ch.new != null)
    );
  };

  useEffect(() => {
    applyFilters();
  }, [auditLogs, startDate, endDate, searchTerm]);

  const applyFilters = () => {
    let filtered = [...auditLogs];

    if (startDate || endDate) {
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.ActivityDateTime);
        if (startDate && endDate) {
          return logDate >= startDate && logDate <= endDate;
        } else if (startDate) {
          return logDate >= startDate;
        } else if (endDate) {
          return logDate <= endDate;
        }
        return true;
      });
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((log) => {
        return (
          log.ActivityName?.toLowerCase().includes(searchLower) ||
          log.ActivityDoneBy?.toLowerCase().includes(searchLower) ||
          log.EntityName?.toLowerCase().includes(searchLower) ||
          log.EntityType?.toLowerCase().includes(searchLower) ||
          log.ActivityReason?.toLowerCase().includes(searchLower) ||
          log.ModuleName?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredLogs(filtered);
  };

  const toggleRowExpansion = (logId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateTimeString;
    }
  };

  const formatDateHeader = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const isToday = date.toDateString() === today.toDateString();
      const isYesterday = date.toDateString() === yesterday.toDateString();

      if (isToday) {
        return (
          "TODAY, " +
          date
            .toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
            .toUpperCase()
        );
      } else if (isYesterday) {
        return (
          "YESTERDAY, " +
          date
            .toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
            .toUpperCase()
        );
      } else {
        return date
          .toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })
          .toUpperCase();
      }
    } catch {
      return dateTimeString;
    }
  };

  const getDateGroupColor = (dateIndex) => {
    const colors = [
      "#667eea",
      "#764ba2",
      "#f093fb",
      "#4facfe",
      "#00f2fe",
      "#43e97b",
      "#fa709a",
      "#fee140",
      "#30cfd0",
      "#330867",
    ];
    return colors[dateIndex % colors.length];
  };

  const groupedLogs = useMemo(() => {
    const groups = {};
    filteredLogs.forEach((log) => {
      const dateKey = formatDateHeader(log.ActivityDateTime);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(log);
    });
    return groups;
  }, [filteredLogs]);

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
  };

  const [exportLoading, setExportLoading] = useState(false);

  const formatExportDateTime = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const exportToExcel = async () => {
    const useFolderApi = folderId != null && String(folderId).trim() !== "";
    setExportLoading(true);
    try {
      const rows = await fetchExportData({
        organizationId,
        folderId,
        documentId,
        studyId,
      });

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Audit Log", { views: [{ state: "frozen", ySplit: 5 }] });

      const exportDateTime = formatExportDateTime();
      ws.addRow(["Exporting Date & Time", exportDateTime]);
      const firstRow = rows[0] || {};
      const docOrFolderLabel = useFolderApi ? "Folder Name" : "Document Name";
      const docOrFolderValue = useFolderApi
        ? (firstRow.FolderName ?? "")
        : (firstRow.DocumentName ?? "");
      ws.addRow([docOrFolderLabel, docOrFolderValue]);
      ws.addRow(["Study Name", firstRow.StudyName ?? ""]);
      ws.addRow([]);

      const dataHeaders = rows.length > 0 ? Object.keys(rows[0]) : EXPORT_COLUMNS;
      const headerRow = ws.addRow(dataHeaders);
      const headerFill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4472C4" },
      };
      headerRow.eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      });
      rows.forEach((row) => {
        const values = dataHeaders.map((col) => row[col] ?? "");
        ws.addRow(values);
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `Audit_Log_${new Date().toISOString().split("T")[0]}.xlsx`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeader = () => (
    <>
      <h5 className="modal-title">
        <i className="fas fa-history me-2"></i>Audit Log
      </h5>
      {variant === "modal" && onClose && (
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          aria-label="Close"
        ></button>
      )}
    </>
  );

  const renderBody = () => (
    <div className="audit-log-body d-flex gap-3 flex-grow-1 overflow-hidden" style={{ height: "100%" }}>
        {showFilters && (
          <div className="audit-log-sidebar-card">
            <div className="sidebar-filters-content">
              <h6 className="sidebar-title">
                <i className="fas fa-filter me-2"></i>Filters
              </h6>
              <div className="mb-3">
                <label className="form-label small fw-semibold mb-2">
                  Date Range
                </label>
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
              <div className="mb-3">
                <label className="form-label small fw-semibold mb-2">
                  Search
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search audit logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {(startDate || endDate || searchTerm) && (
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={clearFilters}
                >
                  <i className="fas fa-times me-1"></i>Clear Filters
                </button>
              )}
              <div className="mt-3 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-sm btn-success w-100"
                  onClick={exportToExcel}
                  disabled={exportLoading || !organizationId}
                >
                  <i className={`fas fa-file-excel me-1 ${exportLoading ? "fa-spin" : ""}`}></i>
                  {exportLoading ? "Exporting..." : "Export Excel"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="audit-log-content-card flex-grow-1 overflow-auto">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading audit logs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <p className="text-muted">{error}</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">No audit logs found</p>
            </div>
          ) : (
            <div className="audit-log-timeline">
              {Object.entries(groupedLogs).map(
                ([dateHeader, logs], dateIndex) => {
                  const dateGroupColor = getDateGroupColor(dateIndex);
                  return (
                    <div
                      key={dateHeader}
                      className="date-group"
                      style={{ borderColor: dateGroupColor }}
                    >
                      <div className="date-header">{dateHeader}</div>
                      <div className="date-cards-wrapper">
                        {logs.map((log) => {
                          const isExpanded = expandedRows.has(log.LogId);
                          const avatarColor = getAvatarColor(log.ActivityDoneBy);
                          const valueChanges = log[valueChangesKey];
                          const hasValueChanges = hasMeaningfulValueChanges(valueChanges);

                          return (
                            <div
                              key={log.LogId}
                              className={`audit-log-card ${isExpanded ? "expanded" : ""}`}
                              style={{ "--dot-color": dateGroupColor }}
                            >
                              <div className="card-content">
                                <div className="card-title-section">
                                  <h6 className="activity-title">
                                    {log.ActivityName}
                                  </h6>
                                  <div className="user-info-compact">
                                    <div
                                      className="user-avatar-small"
                                      style={{ backgroundColor: avatarColor }}
                                    >
                                      {getInitials(log.ActivityDoneBy)}
                                    </div>
                                    <span className="user-name-small">
                                      {log.ActivityDoneBy}
                                    </span>
                                    <span className="meta-separator">•</span>
                                    <span className="time-info-small">
                                      {formatTime(log.ActivityDateTime)}
                                    </span>
                                  </div>
                                  <div className="entity-info">
                                    <span className="entity-name">
                                      {log.EntityName}
                                    </span>
                                    <span className="entity-type">
                                      ({log.EntityType})
                                    </span>
                                    {log.EntityType === "Document" && (log.FolderName != null || log.VersionNo != null) && (
                                      <div className="entity-doc-meta">
                                        {log.FolderName != null && (
                                          <span className="entity-folder">
                                            Folder: {log.FolderName}
                                          </span>
                                        )}
                                        {log.VersionNo != null && (
                                          <span className="entity-version">
                                            Version {log.VersionNo}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {log.ActivityReason && (
                                  <div className="reason-section-main">
                                    <div className="reason-quote">
                                      "{log.ActivityReason}"
                                    </div>
                                  </div>
                                )}

                                {hasValueChanges && isExpanded && (
                                  <div className="card-details">
                                    <div className="detail-section">
                                      <div className="value-changes-table">
                                        <div className="table-header">
                                          <div className="table-cell header-cell">
                                            FIELD
                                          </div>
                                          <div className="table-cell header-cell">
                                            PREVIOUS VALUE
                                          </div>
                                          <div className="table-cell header-cell">
                                            NEW VALUE
                                          </div>
                                        </div>
                                        {Object.entries(valueChanges)
                                          .filter(
                                            ([, ch]) =>
                                              ch != null && (ch.old != null || ch.new != null)
                                          )
                                          .map(([field, changes]) => (
                                            <div key={field} className="table-row">
                                              <div
                                                className="table-cell field-cell"
                                                data-label="FIELD"
                                              >
                                                {field}
                                              </div>
                                              <div
                                                className="table-cell old-value-cell"
                                                data-label="PREVIOUS VALUE"
                                              >
                                                {changes?.old ?? (
                                                  <span className="empty-value">
                                                    (empty)
                                                  </span>
                                                )}
                                              </div>
                                              <div
                                                className="table-cell new-value-cell"
                                                data-label="NEW VALUE"
                                              >
                                                {changes?.new ?? (
                                                  <span className="empty-value">
                                                    (empty)
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {hasValueChanges && (
                                  <button
                                    className="expand-button"
                                    onClick={() =>
                                      toggleRowExpansion(log.LogId)
                                    }
                                  >
                                    <i
                                      className={`fas fa-chevron-${
                                        isExpanded ? "up" : "down"
                                      }`}
                                    ></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      </div>
  );

  if (variant === "modal") {
    if (!isOpen) return null;
    return (
      <div
        className="modal fade show d-block audit-log-modal"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        onClick={(e) => {
          if (e.target.classList.contains("modal") && onClose) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content" style={{ height: "90vh" }}>
            <div className="modal-header">{renderHeader()}</div>
            <div
              className="modal-body p-3 d-flex gap-3"
              style={{ height: "calc(90vh - 120px)" }}
            >
              {renderBody()}
            </div>
            <div className="modal-footer">
              {onClose && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-log-inline container-fluid py-3">
      <div className="d-flex flex-column" style={{ minHeight: "70vh" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          {renderHeader()}
        </div>
        <div className="flex-grow-1 mt-3 overflow-hidden">{renderBody()}</div>
      </div>
    </div>
  );
};

export default ISFAuditLog;
